import React, { useState, useMemo } from "react";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";
import AccountsTable from "./AccountsTable";
import "../assets/css/DigixLeads.css";

/**
 * AccountsLeadsPage
 *
 * Self-contained list for Accounts: frontend filters, New Account button, global search, table, pagination.
 * UI: Row 1 = Account Name, Username, Clear (left) | New Account (right). Row 2 = Search... input.
 */
function AccountsLeadsPage({
  accounts = [],
  customers = [],
  rowsPerPage = 5,
  onViewAccount,
  onEditAccount,
  onDeleteAccount,
  onAddAccountClick,
}) {
  const [filters, setFilters] = useState({
    accountName: "",
    accountUsername: "",
    freeSearch: "",
  });
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState("asc");
  const [currentPage, setCurrentPage] = useState(1);
  const [columnWidths, setColumnWidths] = useState({});
  const resizeRef = React.useRef({ startX: 0, startWidth: 0, columnIndex: null });

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
    setCurrentPage(1);
  };

  const handleClearFilters = () => {
    setFilters({ accountName: "", accountUsername: "", freeSearch: "" });
    setSortColumn(null);
    setSortDirection("asc");
    setCurrentPage(1);
  };

  const customersById = useMemo(() => {
    const map = new Map();
    (customers || []).forEach((c) => {
      const id = c?.id ?? c?.client_id ?? c?.client_id;
      const name =
        c?.name ??
        c?.Customer_Name ??
        c?.customer_name ??
        c?.companyName ??
        "";
      if (id != null && String(name).trim() !== "") {
        map.set(String(id), String(name));
      }
    });
    return map;
  }, [customers]);

  const baseAccounts = useMemo(() => {
    return (accounts || []).map((a) => {
      const customerId = a?.clientId ?? a?.client_id ?? a?.client_id ?? a?.client?.client_id;
      const fromApi =
        a?.customerName ??
        a?.customer_name ??
        a?.Customer_Name ??
        a?.client?.Customer_Name ??
        a?.client?.customer_name ??
        "";
      const fromCustomers = customerId != null ? customersById.get(String(customerId)) : undefined;
      const finalName = (String(fromApi).trim() !== "" && fromApi !== "—") ? fromApi : (fromCustomers || "—");
      return {
        ...a,
        customerId: customerId ?? a?.clientId ?? a?.client_id ?? null,
        customerName: finalName,
        customer_name: finalName
      };
    });
  }, [accounts, customersById]);

  const fieldFilteredAccounts = useMemo(() => {
    const accountNameFilter = String(filters.accountName || "").trim().toLowerCase();
    const usernameFilter = String(filters.accountUsername || "").trim().toLowerCase();
    if (!accountNameFilter && !usernameFilter) return baseAccounts;

    return baseAccounts.filter((account) => {
      const accountName = String(account.accountName ?? account.account_name ?? "").toLowerCase();
      const username = String(account.username ?? "").toLowerCase();
      const nameOk = !accountNameFilter || accountName.includes(accountNameFilter);
      const userOk = !usernameFilter || username.includes(usernameFilter);
      return nameOk && userOk;
    });
  }, [baseAccounts, filters.accountName, filters.accountUsername]);

  // Free search across key account fields (frontend, on type)
  const freeSearchFiltered = useMemo(() => {
    const searchValue = String(filters.freeSearch || "").toLowerCase().trim();
    if (!searchValue) return fieldFilteredAccounts;

    return fieldFilteredAccounts.filter((account) => {
      const accountName = String(
        account.accountName ?? account.account_name ?? ""
      )
        .trim()
        .toLowerCase();
      const username = String(account.username ?? "").trim().toLowerCase();
      const password = String(account.password ?? "").trim().toLowerCase();
      const businessUrl = String(
        account.businessUrl ?? account.business_url ?? ""
      )
        .trim()
        .toLowerCase();
      const profileUrl = String(
        account.profileUrl ?? account.profile_url ?? ""
      )
        .trim()
        .toLowerCase();
      const contact = String(
        account.contact ??
          account.contact_name ??
          account.contactPhone ??
          account.contact_phone ??
          ""
      )
        .trim()
        .toLowerCase();
      const customerName = String(
        account.customerName ?? account.customer_name ?? ""
      )
        .trim()
        .toLowerCase();
      const platform = String(account.platform ?? "").trim().toLowerCase();
      const status = String(
        account.status ??
          account.Status ??
          account.account_status ??
          account.Account_Status ??
          ""
      )
        .trim()
        .toLowerCase();

      return (
        accountName.includes(searchValue) ||
        username.includes(searchValue) ||
        password.includes(searchValue) ||
        businessUrl.includes(searchValue) ||
        profileUrl.includes(searchValue) ||
        contact.includes(searchValue) ||
        customerName.includes(searchValue) ||
        platform.includes(searchValue) ||
        status.includes(searchValue)
      );
    });
  }, [fieldFilteredAccounts, filters.freeSearch]);

  const displayedAccounts = useMemo(() => {
    if (!sortColumn) return freeSearchFiltered;
    const sortValue = (account, columnId) => {
      switch (columnId) {
        case "accountName":
          return String(account.accountName ?? account.account_name ?? "").toLowerCase();
        case "username":
          return String(account.username ?? "").toLowerCase();
        case "url":
          return String(account.profileUrl ?? account.profile_url ?? "").toLowerCase();
        case "email":
          return String(account.emailUsed ?? account.email_used ?? "").toLowerCase();
        case "contact":
          return String(
            account.contact ??
              account.contact_name ??
              account.contactPhone ??
              account.contact_phone ??
              ""
          ).toLowerCase();
        case "notes":
          return String(account.notes ?? "").toLowerCase();
        case "platform":
          return String(account.platform ?? "").toLowerCase();
        default:
          return "";
      }
    };
    const mult = sortDirection === "desc" ? -1 : 1;
    return [...freeSearchFiltered].sort((a, b) => {
      const va = sortValue(a, sortColumn);
      const vb = sortValue(b, sortColumn);
      if (va < vb) return -1 * mult;
      if (va > vb) return 1 * mult;
      const ida = Number(a.id ?? a.account_id ?? 0);
      const idb = Number(b.id ?? b.account_id ?? 0);
      return (ida - idb) * mult;
    });
  }, [freeSearchFiltered, sortColumn, sortDirection]);

  const handleSort = (columnId) => {
    setCurrentPage(1);
    if (sortColumn === columnId) {
      setSortDirection((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortColumn(columnId);
      setSortDirection("asc");
    }
  };

  const totalPages = Math.ceil(displayedAccounts.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const currentRows = displayedAccounts.slice(startIndex, endIndex);
  const showPagination = displayedAccounts.length >= rowsPerPage;
  const startRow = displayedAccounts.length > 0 ? startIndex + 1 : 0;
  const endRow = Math.min(endIndex, displayedAccounts.length);
  const totalRows = displayedAccounts.length;

  const handlePreviousPage = () => {
    if (currentPage > 1) setCurrentPage((p) => p - 1);
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) setCurrentPage((p) => p + 1);
  };

  const handlePageClick = (page) => {
    setCurrentPage(page);
  };

  const getPageNumbers = () => {
    if (totalPages <= 5) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }
    if (currentPage <= 3) {
      return [1, 2, 3, "...", totalPages];
    }
    if (currentPage >= totalPages - 2) {
      return [1, "...", totalPages - 2, totalPages - 1, totalPages];
    }
    return [
      1,
      "...",
      currentPage - 1,
      currentPage,
      currentPage + 1,
      "...",
      totalPages,
    ];
  };

  const getColumnWidth = (columnIndex) => {
    if (columnWidths[columnIndex]) {
      return `${columnWidths[columnIndex]}px`;
    }
    return undefined;
  };

  const getColumnMinWidth = (columnIndex) => {
    if (columnWidths[columnIndex]) {
      return `${columnWidths[columnIndex]}px`;
    }
    return undefined;
  };

  const handleResizeStart = (e, columnIndex) => {
    e.preventDefault();
    e.stopPropagation();

    const th = e.currentTarget.parentElement;
    if (!th) return;

    const startWidth = th.offsetWidth;
    const startX = e.clientX;

    resizeRef.current = {
      startX,
      startWidth,
      columnIndex
    };

    document.body.classList.add("resizing");

    const handleMouseMove = (moveEvent) => {
      if (resizeRef.current.columnIndex === null) return;
      const diff = moveEvent.clientX - resizeRef.current.startX;
      const newWidth = Math.max(80, resizeRef.current.startWidth + diff);

      setColumnWidths((prev) => ({
        ...prev,
        [resizeRef.current.columnIndex]: newWidth
      }));
    };

    const handleMouseUp = () => {
      resizeRef.current = { startX: 0, startWidth: 0, columnIndex: null };
      document.body.classList.remove("resizing");
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  };

  return (
    <div className="accounts-page">
      {/* Row 1: Account Name, Username, Search (left) | New Account (right) */}
      <div className="search-actions-row">
        <div className="search-filters-row">
          <input
            type="text"
            className="search-filter-input"
            placeholder="Search account name..."
            value={filters.accountName}
            onChange={(e) =>
              handleFilterChange("accountName", e.target.value)
            }
          />
          <input
            type="text"
            className="search-filter-input"
            placeholder="Search username..."
            value={filters.accountUsername}
            onChange={(e) =>
              handleFilterChange("accountUsername", e.target.value)
            }
          />
          <button
            type="button"
            className="btn-clear"
            onClick={handleClearFilters}
          >
            Clear
          </button>
        </div>
        <div className="header-actions">
          {onAddAccountClick && (
            <button
              type="button"
              className="btn-add-lead"
              onClick={onAddAccountClick}
            >
              New Account
            </button>
          )}
        </div>
      </div>

      {/* Row 2: Global Search... */}
      <div className="search-row-2">
        <div className="free-search-bar">
          <input
            type="text"
            className="free-search-input"
            placeholder="Search..."
            value={filters.freeSearch}
            onChange={(e) => handleFilterChange("freeSearch", e.target.value)}
            title="Search across all columns"
          />
        </div>
      </div>

      <div className="table-wrapper">
        <div className="table-container">
          <AccountsTable
            rows={currentRows}
            sortColumn={sortColumn}
            sortDirection={sortDirection}
            onSort={handleSort}
            getColumnWidth={getColumnWidth}
            getColumnMinWidth={getColumnMinWidth}
            handleResizeStart={handleResizeStart}
            handleView={onViewAccount}
            handleEdit={onEditAccount}
            handleDelete={onDeleteAccount}
          />
        </div>
      </div>

      {showPagination && (
        <div className="pagination">
          <button
            className="pagination-btn pagination-arrow"
            onClick={handlePreviousPage}
            disabled={currentPage === 1}
            aria-label="Previous page"
          >
            <FaChevronLeft />
          </button>
          <div className="page-numbers">
            {getPageNumbers().map((page, index) =>
              page === "..." ? (
                <span
                  key={`ellipsis-${index}`}
                  className="page-number ellipsis"
                >
                  ...
                </span>
              ) : (
                <span
                  key={page}
                  className={`page-number ${
                    currentPage === page ? "active" : ""
                  }`}
                  onClick={() => handlePageClick(page)}
                >
                  {page}
                </span>
              )
            )}
          </div>
          <button
            className="pagination-btn pagination-arrow"
            onClick={handleNextPage}
            disabled={currentPage === totalPages}
            aria-label="Next page"
          >
            <FaChevronRight />
          </button>
          {totalRows > 0 && (
            <div className="pagination-info">
              {startRow}-{endRow} of {totalRows} items
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default AccountsLeadsPage;

